import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import api from "../../services/api";
import { SessionInfo, SessionsResponse } from "../../api/types";

const PAGE_LIMIT = 10;

export function Sessions() {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<string[]>([""]);
  const [searchEmailInput, setSearchEmailInput] = useState("");
  const [searchEmail, setSearchEmail] = useState("");

  const logError = (label: string, error: unknown) => {
    if (isAxiosError(error)) {
      console.warn(label, error.response?.status ?? "unknown", error.message);
      return;
    }
    if (error instanceof Error) {
      console.warn(label, error.message);
      return;
    }
    console.warn(label);
  };

  const loadSessions = async (pageToLoad = 1, emailFilter = searchEmail) => {
    const cursor = pageCursors[pageToLoad - 1] ?? "";
    if (pageToLoad > 1 && !cursor) return;
    pageToLoad === 1 ? setLoading(true) : setLoadingMore(true);
    setMessage(null);
    try {
      const query = new URLSearchParams();
      query.set("limit", String(PAGE_LIMIT));
      if (cursor) query.set("cursor", cursor);
      if (emailFilter.trim()) query.set("email", emailFilter.trim());
      const { data } = await api.get<SessionsResponse>(`/admin/sessions?${query.toString()}`);
      const next = data.nextCursor || "";
      setSessions(data.data);
      setNextCursor(next);
      setPage(pageToLoad);
      setPageCursors((prev) => {
        const updated = [...prev];
        updated[pageToLoad - 1] = cursor;
        updated[pageToLoad] = next;
        return updated;
      });
    } catch (error) {
      logError("Failed to load sessions", error);
      setMessage(t("admin.sessions.loadError"));
    } finally {
      pageToLoad === 1 ? setLoading(false) : setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadSessions(1, "");
  }, []);

  const handleRevokeSession = async (session: SessionInfo) => {
    if (!confirm(t("admin.sessions.confirmClose"))) return;
    try {
      await api.delete(`/admin/sessions/${session.id}`);
      setSessions((prev) => prev.filter((item) => item.id !== session.id));
      setMessage(t("admin.sessions.closed"));
    } catch (error) {
      logError("Failed to revoke session", error);
      setMessage(t("admin.sessions.closeError"));
    }
  };

  const handleRevokeUserSessions = async (session: SessionInfo) => {
    if (!session.userId) return;
    const label = session.userName || session.userEmail || `ID ${session.userId}`;
    if (!confirm(t("admin.sessions.confirmCloseAll", { label }))) return;
    try {
      await api.post(`/admin/users/${session.userId}/sessions/revoke`);
      setSessions((prev) => prev.filter((item) => item.userId !== session.userId));
      setMessage(t("admin.sessions.closedAll"));
    } catch (error) {
      logError("Failed to revoke user sessions", error);
      setMessage(t("admin.sessions.closeAllError"));
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return "—";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "—";
    return parsed.toLocaleString();
  };

  const truncate = (value?: string, limit = 80) => {
    if (!value) return t("admin.sessions.notAvailable");
    if (value.length <= limit) return value;
    return `${value.slice(0, limit)}...`;
  };

  return (
    <div className="shell">
      <div className="header">
        <div className="header-title">
          <h1>{t("admin.sessions.title")}</h1>
          <p className="muted">{t("admin.sessions.subtitle")}</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const normalized = searchEmailInput.trim();
              setSearchEmail(normalized);
              setPageCursors([""]);
              setNextCursor("");
              loadSessions(1, normalized);
            }}
            style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
          >
            <input
              value={searchEmailInput}
              onChange={(e) => setSearchEmailInput(e.target.value)}
              placeholder={t("admin.sessions.searchPlaceholder")}
              style={{ width: '220px' }}
            />
            <button className="secondary" type="submit">
              {t("admin.sessions.search")}
            </button>
            {searchEmail && (
              <button
                className="secondary"
                type="button"
                onClick={() => {
                  setSearchEmailInput("");
                  setSearchEmail("");
                  setPageCursors([""]);
                  setNextCursor("");
                  loadSessions(1, "");
                }}
              >
                {t("admin.sessions.clear")}
              </button>
            )}
          </form>
          <button className="secondary" onClick={() => loadSessions(1, searchEmail)} disabled={loading}>
            {t("admin.sessions.refresh")}
          </button>
        </div>
      </div>

      {message && (
        <div className="info" style={{ marginBottom: '1.5rem' }}>
          {message}
        </div>
      )}

      <div className="stack">
        {loading ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
            <p className="muted">{t("admin.sessions.loading")}</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
            <p className="muted">{t("admin.sessions.empty")}</p>
          </div>
        ) : (
          <div className="grid">
            {sessions.map((session) => (
              <div key={session.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem' }}>
                      {session.userName || session.userEmail || t("admin.sessions.unknownUser")}
                    </h3>
                    <p className="muted" style={{ fontSize: '0.75rem' }}>{session.userEmail}</p>
                  </div>
                  <span style={{ 
                    padding: '0.2rem 0.4rem', 
                    borderRadius: '4px', 
                    fontSize: '0.625rem',
                    backgroundColor: '#eff6ff',
                    color: '#3b82f6',
                    fontWeight: '700'
                  }}>
                    ID: {session.userId ?? "—"}
                  </span>
                </div>

                <div style={{ fontSize: '0.8125rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <span className="muted" style={{ minWidth: '80px' }}>{t("admin.sessions.device")}:</span>
                    <span style={{ wordBreak: 'break-all' }}>{truncate(session.userAgent, 60)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <span className="muted" style={{ minWidth: '80px' }}>{t("admin.sessions.ip")}:</span>
                    <span>{session.ip || t("admin.sessions.notAvailable")}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <span className="muted" style={{ minWidth: '80px' }}>{t("admin.sessions.startedAt")}:</span>
                    <span>{formatDate(session.createdAt)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <span className="muted" style={{ minWidth: '80px' }}>{t("admin.sessions.activity")}:</span>
                    <span style={{ color: 'var(--accent)', fontWeight: '500' }}>{formatDate(session.lastActivity)}</span>
                  </div>
                </div>

                <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem' }}>
                  <button className="secondary small" onClick={() => handleRevokeSession(session)} style={{ fontSize: '0.75rem' }}>
                    {t("admin.sessions.close")}
                  </button>
                  <button className="secondary small" onClick={() => handleRevokeUserSessions(session)} style={{ fontSize: '0.75rem', color: '#ef4444', borderColor: '#fecaca' }}>
                    {t("admin.sessions.closeAll")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem' }}>
          <span className="muted" style={{ fontSize: '0.85rem' }}>
            {t("admin.sessions.page", { page })} {nextCursor ? "" : t("admin.sessions.endOfList")}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="secondary"
              disabled={page <= 1 || loadingMore}
              onClick={() => loadSessions(page - 1, searchEmail)}
            >
              {t("common.previous")}
            </button>
            <button
              className="secondary"
              disabled={!nextCursor || loadingMore}
              onClick={() => loadSessions(page + 1, searchEmail)}
            >
              {t("common.next")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
